import * as cacheManager from "../cacheManager";
import * as dateUtil from "../dateUtil";
import * as parseDate from "../parseDate";
import * as userContentKeys from "../../enums/userContentKeys";
import instance from "./instance";
import sortBy from "lodash.sortby";

export const GROUP_KEY_ROOT = "group-";

function parseUserDeviceDate(rawDeviceData) {
	return {
		...rawDeviceData,
		create_time: parseDate.safeParseDate(rawDeviceData.create_time),
	};
}

function setContentField(fieldName, content) {
	if (content[fieldName]) {
		let contentValue = content[fieldName];
		if (Array.isArray(contentValue)) {
			contentValue = {};
		}
		content[fieldName] = contentValue;
	}
}

function parseUserContent(rawUserData) {
	if (rawUserData.content === null || rawUserData.content === undefined) {
		return rawUserData;
	}

	const content = { ...rawUserData.content };
	setContentField(userContentKeys.DEFAULT_TARGET_FIELDS, content);
	setContentField(userContentKeys.DEFAULT_TARGET_COLUMN_ORDER, content);
	setContentField(userContentKeys.DEFAULT_TARGET_COLUMN_WIDTHS, content);
	setContentField(userContentKeys.DEFAULT_PROFILE_LANDING_TABS, content);
	setContentField(userContentKeys.TARGET_LAYOUT, content);

	return {
		...rawUserData,
		content,
	};
}

export function parseUserDate(rawUserData) {
	return {
		...rawUserData,
		last_lit_history: parseDate.safeParseDate(rawUserData.last_lit_history),
		last_login: parseDate.safeParseDate(rawUserData.last_login),
		start_time: parseDate.safeParseDate(rawUserData.start_time),
		password_reset: parseDate.safeParseDate(rawUserData.password_reset),
		practice_areas: rawUserData.practice_areas.map(practiceArea => ({
			...practiceArea,
			usage: {
				...practiceArea.usage,
				dates: practiceArea.usage.dates.map(dateString =>
					parseDate.safeParseDate(dateString),
				),
			},
		})),
	};
}

function parseLitigationDate(rawLitigationData) {
	return rawLitigationData.map(l => ({
		...l,
		case_filing_date: dateUtil.parseApiDate(l.case_filing_date),
		case_termination_date: dateUtil.parseApiDate(l.case_termination_date),
		party_start_date: dateUtil.parseApiDate(l.party_start_date),
		party_end_date: dateUtil.parseApiDate(l.party_end_date),
	}));
}

function parseFirmDate(rawFirmData) {
	return rawFirmData.map(firm => ({
		...firm,
		start_date: dateUtil.parseApiDate(firm.start_date),
		end_date: dateUtil.parseApiDate(firm.end_date),
	}));
}

export function find(email) {
	return instance
		.request({
			url: "/users/find",
			method: "POST",
			data: {
				email,
			},
		})
		.then(response => response.data);
}

export function me() {
	return instance
		.request({
			url: "/users/me",
			method: "POST",
		})
		.then(response => parseUserDate(response.data))
		.then(res => {
			const lastLogin = res.last_login;
			const lastLoginDate = new Date(lastLogin);
			const currentDate = new Date();
			const differenceInMilliseconds = currentDate - lastLoginDate;
			const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);
			const remainingTimeInMilliseconds = (48 - differenceInHours) * 60 * 60 * 1000;

			if (remainingTimeInMilliseconds > 0) {
				setTimeout(() => {
					me();
				}, remainingTimeInMilliseconds);
			}
			return parseUserContent(res);
		});
}

export function networkCatalog(groupId, includeUsage, libraryName) {
	return instance
		.request({
			url: "/users/catalog",
			method: "POST",
			data: {
				group_id: groupId,
				includeUsage,
				library_name: libraryName,
			},
		})
		.then(response => ({
			total: response.data.total,
			users: response.data.users.map(user => {
				const data = parseUserDate(user);
				return parseUserContent(data);
			}),
		}));
}

export function catalog(groupId, includeUsage = false, libraryName = null) {
	return cacheManager.getGroup(groupId, includeUsage, libraryName);
}

export function get(user_id) {
	return instance
		.request({
			url: "/users/get",
			method: "POST",
			data: {
				user_id,
			},
		})
		.then(response => parseUserDate(response.data));
}

export function update(
	user_id,
	email,
	firstName,
	lastName,
	firm,
	phone,
	beta,
	practiceAreas,
	content,
	downloadNotification,
	lastLitHistory, // truthy if last_lit_history should be updated to current date
	jobRole,
) {
	return instance
		.request({
			url: "/users/update",
			method: "POST",
			data: {
				beta,
				content,
				email,
				firm,
				first_name: firstName,
				last_name: lastName,
				phone,
				practice_areas: practiceAreas,
				user_id,
				download_notification: downloadNotification,
				last_lit_history: !!lastLitHistory,
				job_role: jobRole,
			},
		})
		.then(response => parseUserDate(response.data))
		.then(res => parseUserContent(res));
}

export function remove(user_id) {
	return instance
		.request({
			url: "/users/remove",
			method: "POST",
			data: {
				user_id,
			},
		})
		.then(() => {});
}

export function resetPassword(user_id) {
	return instance
		.request({
			url: "/users/reset_password",
			method: "POST",
			data: {
				user_id,
			},
		})
		.then(() => {});
}

export function resetPasswords(user_ids, cc_sender) {
	return instance
		.request({
			url: "/users/reset_passwords",
			method: "POST",
			data: {
				user_ids,
				cc_sender,
			},
		})
		.then(() => {});
}

export function setPassword(password) {
	return instance
		.request({
			url: "/users/set_password",
			method: "POST",
			data: {
				password,
			},
		})
		.then(() => {});
}

export function sendEmail(to_user_id, mail_name, subject, message) {
	return instance
		.request({
			url: "/users/send_email",
			method: "POST",
			data: {
				to_user_id,
				mail_name,
				subject,
				message,
			},
		})
		.then(() => {});
}

export function sendEmailWelcome(to_user_ids, cc_sender) {
	return instance
		.request({
			url: "/users/send_email_welcome",
			method: "POST",
			data: {
				to_user_ids,
				cc_sender,
			},
		})
		.then(() => {});
}

export function sendDocketReport(user_id, practice_area) {
	return instance
		.request({
			url: "/users/send_docket_report",
			method: "POST",
			data: {
				user_id,
				practice_area,
			},
		})
		.then(() => {});
}

export function requestHelp(email, phone, subject, request, urgent, cc_admin) {
	return instance
		.request({
			url: "/users/request_help",
			method: "POST",
			data: {
				email,
				phone,
				subject,
				request,
				urgent,
				cc_admin,
			},
		})
		.then(() => {});
}

export function devices() {
	return instance
		.request({
			url: "/users/devices",
			method: "POST",
		})
		.then(response => response.data.map(d => parseUserDeviceDate(d)));
}

export function removeDevice(device_id) {
	return instance
		.request({
			url: "/users/remove_device",
			method: "POST",
			data: {
				device_id,
			},
		})
		.then(() => {});
}

export function sidebar(library_name) {
	return instance
		.request({
			url: "/users/sidebar",
			method: "POST",
			data: {
				library_name,
			},
		})
		.then(response => response.data);
}

export function navigation(target_name, section_name) {
	return instance
		.request({
			url: "/users/navigation",
			method: "POST",
			data: {
				target_name,
				section_name,
			},
		})
		.then(() => {});
}

export function createAuthToken(title) {
	return instance
		.request({
			url: "/users/create_auth_token",
			method: "POST",
			data: {
				title,
			},
		})
		.then(response => response.data);
}

export function logout() {
	return instance
		.request({
			url: "/users/logout",
			method: "POST",
		})
		.then(response => response.data);
}

export function logo(group_id) {
	return instance
		.request({
			url: "/users/logo",
			method: "POST",
			data: {
				group_id,
			},
		})
		.then(response => response.data);
}

export function getTableauTicket() {
	return instance
		.request({
			url: "/users/get_tableau_ticket",
			method: "POST",
		})
		.then(response => response.data);
}

function getCaseParty(caseInfo) {
	return {
		id: caseInfo.case_party_id,
		end_date: caseInfo.party_end_date,
		name: caseInfo.party_name,
		start_date: caseInfo.party_start_date,
	};
}

export function getLitigationHistory(user_id) {
	return instance
		.request({
			url: "/users/get_litigation_history",
			method: "POST",
			data: {
				user_id,
			},
		})
		.then(response => parseLitigationDate(response.data))
		.then(data => {
			const caseMap = new Map();

			data.forEach((caseInfo, idx) => {
				const id = caseInfo.case_id;
				if (caseMap.has(caseInfo.case_id)) {
					const existingValue = caseMap.get(id);
					const party = getCaseParty(caseInfo);
					existingValue.partiesMap.set(party.id, party);
				} else {
					const partiesMap = new Map();
					const party = getCaseParty(caseInfo);
					partiesMap.set(party.id, party);

					const value = {
						filing_date: caseInfo.case_filing_date,
						id: id,
						name: caseInfo.case_name,
						number: caseInfo.case_number,
						partiesMap,
						sort: idx,
						termination_date: caseInfo.case_termination_date,
					};
					caseMap.set(id, value);
				}
			});

			const parsedCases = [];
			caseMap.forEach(value => {
				const caseParties = [];
				value.partiesMap.forEach(party => caseParties.push(party));
				parsedCases.push({
					...value,
					parties: caseParties,
				});
			});

			return sortBy(parsedCases, c => c.sort).map(c => {
				delete c.sort;
				delete c.partiesMap;
				return c;
			});
		});
}

export function updateLitigationHistory(user_id, history) {
	return instance.request({
		url: "/users/update_litigation_history",
		method: "POST",
		data: {
			user_id,
			history,
		},
	});
}

export function getFirmHistory(user_id) {
	return instance
		.request({
			url: "/users/get_firm_history",
			method: "POST",
			data: {
				user_id,
			},
		})
		.then(response => parseFirmDate(response.data));
}

export function updateFirmHistory(userId, history) {
	return instance
		.request({
			url: "/users/update_firm_history",
			method: "POST",
			data: {
				history,
				user_id: userId,
			},
		})
		.then(() => {});
}

export function sharePatentCloud() {
	return instance
		.request({
			url: "/users/share_patentcloud",
			method: "POST",
		})
		.then(response => response.data);
}

export function verifyFirmHistory(userId) {
	return instance
		.request({
			url: "/users/verify_firm_history",
			method: "POST",
			data: {
				user_id: userId,
			},
		})
		.then(response => response.data);
}

export const fetchScore = user_id => {
	return instance
		.request({
			url: "/users/record_score",
			method: "POST",
			data: {
				user_id,
			},
		})
		.then(response => response);
};

export const postScore = (user_id, rejected, url, content, score, feedback) => {
	return instance
		.request({
			url: "/users/record_score",
			method: "POST",
			data: {
				user_id, // User ID
				rejected, // boolean, if they just closed it without answering.
				url, // text, what page they were on when they submitted feedback
				content, // array/object that the front end manages.
				score, // integer, 0-10, optional
				feedback, // text, optional
			},
		})
		.then(() => {});
};
